import { useScrollReveal } from '../../hooks/useScrollReveal';

export interface AIQAItem {
  question: string;
  answer: string;
}

interface AIAnswerBlockProps {
  items: AIQAItem[];
  title?: string;
}

export default function AIAnswerBlock({ items, title = 'Common Questions' }: AIAnswerBlockProps) {
  const headerRef = useScrollReveal();

  return (
    <section className="py-36 bg-white">
      <div className="max-w-7xl mx-auto px-8 lg:px-16">
        {/* Header */}
        <div ref={headerRef} className="reveal-on-scroll mb-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-end">
            <div className="lg:col-span-6">
              <p className="font-sans text-[10px] tracking-[0.22em] uppercase text-bronze mb-4">AI-Structured Answers</p>
              <h2 className="font-serif text-5xl lg:text-6xl font-light text-dark-ink">{title}</h2>
            </div>
            <div className="lg:col-span-6">
              <p className="font-sans text-sm text-warm-gray leading-relaxed max-w-sm lg:ml-auto">
                Clear, direct answers about who we are, what we offer, and what sets our residential architecture apart.
              </p>
            </div>
          </div>
        </div>

        {/* Q&A items */}
        <div className="divide-y divide-stone">
          {items.map((item, i) => (
            <AIItem key={i} item={item} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

function AIItem({ item, index }: { item: AIQAItem; index: number }) {
  const ref = useScrollReveal();

  return (
    <div ref={ref} className={`reveal-on-scroll reveal-delay-${Math.min(index + 1, 6)} py-10`}>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-16">
        {/* Question */}
        <div className="lg:col-span-5 flex items-start gap-4">
          <span className="font-sans text-[10px] tracking-[0.18em] text-bronze uppercase mt-1 flex-shrink-0">Q.</span>
          <h3 className="font-serif text-xl font-light text-dark-ink leading-snug">{item.question}</h3>
        </div>
        {/* Answer */}
        <div className="lg:col-span-7 flex items-start gap-4">
          <span className="font-sans text-[10px] tracking-[0.18em] text-stone uppercase mt-1 flex-shrink-0 hidden lg:block">A.</span>
          <p className="font-sans text-sm text-warm-gray leading-[1.85]">{item.answer}</p>
        </div>
      </div>
    </div>
  );
}
